(ns quuppa.qsm.planner.overlays
  (:require [clojure.string :as str]
            [quuppa.qsm.planner.utils :as utils]))

(defn route-display
  "Display a single route polygon (non-editable).
   Used to show all routes on the floorplan."
  [route-id route selected? color]
  (let [points (:points route)]
    (when (and points (> (count points) 1))
      [:g {:key (str "route-display-" route-id)}
       [:polygon {:points (str/join " " (map (fn [[x y]] (str x "," y)) points))
                  :fill   "none"
                  :stroke color
                  :stroke-width (if selected? 3 2)
                  :stroke-dasharray (if selected? "8,4" "5,3")
                  :style {:pointer-events "none"}}]])))

(defn routes-display-overlay
  "SVG overlay showing all routes for the current level.
   Routes are displayed as closed polygons without fill.
   The selected route is excluded since it's rendered by polygon-editing-overlay."
  [routes selected-route-id img-width img-height]
  (let [w (or img-width 800)
        h (or img-height 600)
        ;; Filter out the selected route - it will be rendered by polygon-editing-overlay
        routes-to-display (remove (fn [[rid _]] (= rid selected-route-id)) routes)
        routes-vec (vec routes)]  ; Keep original for index-based color lookup
    [:svg.routes-display-overlay
     {:viewBox (str "0 0 " w " " h)
      :preserveAspectRatio "none"
      :style {:position "absolute"
              :top 0
              :left 0
              :width "100%"
              :height "100%"
              :pointer-events "none"}}
     (for [[route-id route] routes-to-display
           :let [idx (.indexOf (mapv first routes-vec) route-id)]]
       ^{:key (str "route-" route-id)}
       [route-display route-id route false (utils/get-route-color route idx)])]))


(defn geofence-display
  "Display a single geofence polygon (non-editable).
   Used to show all geofences on the floorplan with semi-transparent fill."
  [geofence-id geofence selected? color]
  (let [points (:points geofence)]
    (when (and points (> (count points) 1))
      [:g {:key (str "geofence-display-" geofence-id)}
       ;; Closed polygon path - with semi-transparent fill
       [:polygon {:points (str/join " " (map (fn [[x y]] (str x "," y)) points))
                  :fill color
                  :fill-opacity 0.2
                  :stroke color
                  :stroke-width (if selected? 3 2)
                  :stroke-dasharray (if selected? "8,4" "5,3")
                  :style {:pointer-events "none"}}]
       ;; Start point marker
       (let [[x y] (first points)]
         [:circle {:cx x :cy y
                   :r (if selected? 6 4)
                   :fill color
                   :stroke "#fff"
                   :stroke-width (if selected? 2 1)
                   :style {:pointer-events "none"}}])])))

(defn geofences-display-overlay
  "SVG overlay showing all geofences for the current level.
   Geofences are displayed as closed polygons with semi-transparent fill.
   The selected geofence is excluded since it's rendered by polygon-editing-overlay."
  [geofences selected-geofence-id img-width img-height]
  (let [w (or img-width 800)
        h (or img-height 600)
        ;; Filter out the selected geofence - it will be rendered by polygon-editing-overlay
        geofences-to-display (remove (fn [[gid _]] (= gid selected-geofence-id)) geofences)
        geofences-vec (vec geofences)]  ; Keep original for index-based color lookup
    [:svg.geofences-display-overlay
     {:viewBox (str "0 0 " w " " h)
      :preserveAspectRatio "none"
      :style {:position "absolute"
              :top 0
              :left 0
              :width "100%"
              :height "100%"
              :pointer-events "none"}}
     (for [[geofence-id geofence] geofences-to-display
           :let [idx (.indexOf (mapv first geofences-vec) geofence-id)]]
       ^{:key (str "geofence-" geofence-id)}
       [geofence-display geofence-id geofence false (utils/get-geofence-color geofence idx)])]))

(defn unit-display
  "Display a single unit polygon (non-editable).
   Used to show all units on the floorplan with semi-transparent fill."
  [unit-id unit selected? color]
  (let [points (:points unit)]
    (when (and points (> (count points) 1))
      [:g {:key (str "unit-display-" unit-id)}
       ;; Closed polygon path - with semi-transparent fill and solid stroke
       [:polygon {:points (str/join " " (map (fn [[x y]] (str x "," y)) points))
                  :fill color
                  :fill-opacity 0.2
                  :stroke color
                  :stroke-width (if selected? 3 2)
                  :style {:pointer-events "none"}}]])))

(defn units-display-overlay
  "SVG overlay showing all units for the current level.
   Units are displayed as closed polygons with semi-transparent fill.
   The selected unit is excluded since it's rendered by polygon-editing-overlay."
  [units selected-unit-id img-width img-height]
  (let [w (or img-width 800)
        h (or img-height 600)
        ;; Filter out the selected unit - it will be rendered by polygon-editing-overlay
        units-to-display (remove (fn [[uid _]] (= uid selected-unit-id)) units)
        units-vec (vec units)]  ; Keep original for index-based color lookup
    [:svg.units-display-overlay
     {:viewBox (str "0 0 " w " " h)
      :preserveAspectRatio "none"
      :style {:position "absolute"
              :top 0
              :left 0
              :width "100%"
              :height "100%"
              :pointer-events "none"}}
     (for [[unit-id unit] units-to-display
           :let [idx (.indexOf (mapv first units-vec) unit-id)]]
       ^{:key (str "unit-" unit-id)}
       [unit-display unit-id unit false (utils/get-unit-color unit idx)])]))

(defn opening-display
  "Display a single opening as a dashed line.
   Used to show all openings on the floorplan."
  [opening-id opening]
  (let [points (:points opening)]
    (when (and points (= (count points) 2))
      (let [[x1 y1] (first points)
            [x2 y2] (second points)]
        [:g {:key (str "opening-display-" opening-id)}
         ;; White background line (solid, thicker)
         [:line {:x1 x1 :y1 y1
                 :x2 x2 :y2 y2
                 :stroke "#ffffff"
                 :stroke-width 5
                 :style {:pointer-events "none"}}]
         ;; Green dashed line on top
         [:line {:x1 x1 :y1 y1
                 :x2 x2 :y2 y2
                 :stroke "#22c55e"  ; green
                 :stroke-width 3
                 :stroke-dasharray "8,4"
                 :style {:pointer-events "none"}}]]))))

(defn openings-display-overlay
  "SVG overlay showing all openings for the current level.
   Openings are displayed as dashed lines."
  [openings img-width img-height]
  (let [w (or img-width 800)
        h (or img-height 600)]
    [:svg.openings-display-overlay
     {:viewBox (str "0 0 " w " " h)
      :preserveAspectRatio "none"
      :style {:position "absolute"
              :top 0
              :left 0
              :width "100%"
              :height "100%"
              :pointer-events "none"}}
     (for [[opening-id opening] openings]
       ^{:key (str "opening-" opening-id)}
       [opening-display opening-id opening])]))

(defn locator-display
  "Display a single locator as a green circle."
  [locator-id locator selected?]
  (let [x (:x locator)
        y (:y locator)]
    [:g {:key (str "locator-display-" locator-id)}
     ;; Main circle
     [:circle {:cx x
               :cy y
               :r (if selected? 8 6)
               :fill "#28a745"
               :fill-opacity 0.8
               :stroke "#155724"
               :stroke-width (if selected? 2 1)
               :style {:pointer-events "none"}}]
     ;; Center dot
     [:circle {:cx x
               :cy y
               :r 2
               :fill "#155724"
               :style {:pointer-events "none"}}]]))

(defn locators-display-overlay
  "SVG overlay showing all locators for the current level.
   Locators are displayed as green circles."
  [locators img-width img-height selected-locator-id]
  (let [w (or img-width 800)
        h (or img-height 600)]
    [:svg.locators-display-overlay
     {:viewBox (str "0 0 " w " " h)
      :preserveAspectRatio "none"
      :style {:position "absolute"
              :top 0
              :left 0
              :width "100%"
              :height "100%"
              :pointer-events "none"}}
     (for [[locator-id locator] locators]
       ^{:key (str "locator-" locator-id)}
       [locator-display locator-id locator (= locator-id selected-locator-id)])]))

